import { GateStrip } from './GateStrip';
import { ResultsTable } from './ResultsTable';
import type { PostingListItem } from './types';

/**
 * The worked example on the landing page — rendered by the PRODUCT'S OWN components.
 *
 * `<ResultsTable>` and `<GateStrip>` here are the same modules the search screen mounts, not a
 * drawing of them. So the columns, their order, which of them sort, the deadline ramp's cut points
 * and the gate sequence are all whatever the product currently does. A screenshot or a hand-built
 * table would start telling a story about a version of the product that no longer exists, and would
 * do it silently — which is the specific failure this avoids.
 *
 * WHAT IS SAMPLE, AND SAID SO. The rows below are invented; nothing anonymous can read the corpus,
 * and the endpoint that can (`/overview`) deliberately discloses no posting. The panel is labelled
 * `דוגמה` in the markup, not only in a caption, so the claim travels with the element.
 *
 * TIME IS A PARAMETER, NOT A CONSTANT. Both components accept `now`, and the sample deadlines are
 * expressed as OFFSETS from it rather than as fixed dates. A landing page carrying a hard-coded
 * `12.06` is stale within the year and quietly says the product is abandoned; and the offsets are
 * chosen to put one row in each pressure band, so the ramp always demonstrates itself no matter when
 * the page is opened.
 */

const DAY = 86_400_000;

/** ISO for `now + days`, at a fixed wall-clock time so the rendered date never depends on the hour. */
function at(now: number, days: number): string {
  const d = new Date(now + days * DAY);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 9, 0, 0)).toISOString();
}

/**
 * Offsets chosen against `pressureStep`'s own cut points (2 / 7 / 30 days), one row per band, so the
 * example shows the whole ramp rather than four rows of the same colour. If those cut points move,
 * this stops demonstrating them — which is why the test asserts the rendered steps, not the offsets.
 */
const SAMPLES: { title: string; publisher: string; city: string; days: number; meeting?: number; clarify?: number }[] = [
  /* Cities that `CITY_REGION` maps to `center`, so the two geography columns agree with each other
     — a sample whose city sat in one region and whose row claimed another would be the one thing on
     this page a reader could catch out. */
  { title: 'אספקה והתקנה של מערכות מיזוג בבנייני ציבור', publisher: 'עיריית כפר סבא', city: 'כפר סבא', days: 1 },
  { title: 'הקמת גשר להולכי רגל מעל מסילת הרכבת', publisher: 'רכבת ישראל', city: 'לוד', days: 5, meeting: 2, clarify: 3 },
  { title: 'מתכנן/ת בכיר/ה — משרה מלאה', publisher: 'עיריית כפר סבא', city: 'כפר סבא', days: 21 },
  { title: 'שירותי ייעוץ בטיחות לפרויקטי תשתית', publisher: 'רכבת ישראל', city: 'ראש העין', days: 48 },
];

export function demoItems(now: number): PostingListItem[] {
  return SAMPLES.map((s, i) => ({
    slug: `demo-${i}`,
    siteId: 'demo',
    kind: s.title.includes('משרה') ? 'job' : 'tender',
    title: s.title,
    category: 'construction',
    categoryLabel: 'בינוי',
    region: 'center',
    city: s.city,
    status: 'open',
    /* Complementary, exactly as the ETL builds them — a tender carries no employmentType and a job
       no tenderType. The table's `תת-סוג` column reads whichever of the two the row has, so a job
       sample left with `tenderType: 'open'` would have shown an em-dash there and demonstrated the
       column empty. */
    tenderType: s.title.includes('משרה') ? null : 'open',
    employmentType: s.title.includes('משרה') ? 'full-time' : null,
    professions: [],
    securityClearanceRequired: null,
    dates: {
      published: at(now, -14),
      lastSubmissionDate: at(now, s.days),
      clarification: s.clarify === undefined ? null : at(now, s.clarify),
      lastUpdate: null,
    },
    meetings: s.meeting === undefined ? [] : [{ date: at(now, s.meeting) }],
    value: null,
    source: { publisher: s.publisher, publisherType: 'municipality', detailLink: null },
    lifecycle: { firstSeen: at(now, -14) },
  }));
}

/** The row whose gates the strip demonstrates: the only sample with more than one date. */
const GATED = 1;

export function LandingDemo({ now = Date.now() }: { now?: number }) {
  const items = demoItems(now);

  return (
    <div className="tenders-demo tenders-lightpin" data-testid="landing-demo">
      <p className="tenders-demo__tag">דוגמה</p>

      {/*
        `onSort` and `onOpen` are no-ops rather than absent: the table takes them as required props,
        and giving it working handlers would put a sort control and four clickable rows in front of
        someone who has no session to run a search with. The headers stay real buttons, so the
        keyboard path is honest about what it does — nothing.
      */}
      <ResultsTable items={items} sort="closing" direction="asc" onSort={() => {}} onOpen={() => {}} now={now} />

      <div className="tenders-demo__gates">
        <p className="tenders-demo__caption">כל מכרז הוא רצף מועדים, לא תאריך אחד:</p>
        <GateStrip item={items[GATED]!} now={now} />
      </div>
    </div>
  );
}
